"use client";

import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useEffect, useRef, useState } from "react";

type Fit = "contain" | "cover" | "fill" | "fit-width" | "fit-height";

// Wraps DotLottie and only mounts it once scrolled near the viewport, so the
// (heavy) animation JSON is not fetched/decoded on initial load for below-the-fold
// players. The wrapper carries the sizing classes and reserves space to avoid CLS.
export function LottiePlayer({
  src,
  className,
  fit,
  align,
}: {
  src: string;
  className?: string;
  fit?: Fit;
  align?: [number, number];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {visible && (
        <DotLottieReact
          src={src}
          loop
          autoplay
          className="h-full w-full"
          layout={fit ? { fit, align: align ?? [0.5, 0.5] } : undefined}
        />
      )}
    </div>
  );
}
